import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'
import { LoaderCircleIcon } from 'lucide-react'

/* Every control is a pill; heights meet the outdoor/gloved-use bar (mobile-first,
   md: is the desktop step down). Focus comes from the global outline rule in
   index.css — no per-component ring classes. */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-45 cursor-pointer [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover active:bg-primary-active',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/80',
        outline:
          'border border-border bg-transparent hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)] active:bg-[color-mix(in_srgb,currentColor_14%,transparent)]',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 active:bg-secondary/60',
        ghost:
          'hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)] active:bg-[color-mix(in_srgb,currentColor_14%,transparent)]',
        // accent-foreground = accent-700-family text: the primary FILL colour
        // is tuned for white text on it, not for being text itself
        link: 'text-accent-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-12 px-5 md:h-11',
        sm: 'h-11 px-4 md:h-10',
        lg: 'h-13 px-6 text-[15px] md:h-12 high-contrast:min-h-14',
        icon: 'h-12 w-12 md:h-11 md:w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Shows a spinner and disables the button — single convention for async actions (UI-03) */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {loading && !asChild ? (
          <>
            <LoaderCircleIcon className="h-4 w-4 animate-spin" />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
